import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { api, query } from '../api/client'
import { useQuery } from '../api/hooks'
import type {
  Basis,
  ClientFundNature,
  Department,
  JournalStatus,
  JournalSummary,
  OpenItem,
  Paged,
  Party,
  PostableAccount,
  PostingPolicy,
  PostingValidation,
} from '../api/types'
import { awaitsReview } from '../api/types'
import { useAuth } from '../auth/AuthContext'
import { P } from '../auth/permissions'
import {
  Empty,
  ErrorBanner,
  Field,
  Loading,
  Modal,
  Money,
  Panel,
  StatusTag,
  endOfMonth,
  formatDate,
  formatDateTime,
  isoDate,
  money,
  startOfMonth,
} from '../components/ui'

/**
 * The general journal — accruals and adjustments, used by one or two people. Daily cash entry
 * happens in the books instead (BUILD_PLAN §3.3).
 */
export function JournalPage() {
  const { can, user } = useAuth()
  const [from, setFrom] = useState(startOfMonth())
  const [to, setTo] = useState(endOfMonth())
  const [status, setStatus] = useState<JournalStatus | ''>('')
  const [basis, setBasis] = useState<Basis | ''>('')
  const [term, setTerm] = useState('')
  const [waitingOnly, setWaitingOnly] = useState(false)
  const [composing, setComposing] = useState(false)
  const [reversing, setReversing] = useState<JournalSummary | null>(null)
  const [reviewError, setReviewError] = useState<unknown>()

  const entries = useQuery<Paged<JournalSummary>>(
    `/journal${query({ from, to, status, basis, term, size: 100 })}`,
    [from, to, status, basis, term],
  )

  const policy = useQuery<PostingPolicy>('/posting-policy', [])

  // The queue is a filter over the list rather than a screen of its own. A draft awaiting a second
  // person is still a draft, and putting it somewhere else would mean the journal no longer showed
  // everything in the journal.
  const waiting = (entries.data?.items ?? []).filter(awaitsReview)
  const shown = waitingOnly ? waiting : (entries.data?.items ?? [])

  /** Stamps or withdraws a sign-off, then reloads so the row re-renders from the server. */
  async function review(entry: JournalSummary, approve: boolean) {
    setReviewError(undefined)
    try {
      await api(`/journal/${entry.id}/review`, { method: approve ? 'POST' : 'DELETE' })
      entries.reload()
    } catch (caught) {
      setReviewError(caught)
    }
  }

  return (
    <>
      <div className="toolbar" style={{ marginBottom: 14 }}>
        <div className="field">
          <label>From</label>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </div>
        <div className="field">
          <label>To</label>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </div>
        <div className="field">
          <label>Status</label>
          <select value={status} onChange={(e) => setStatus(e.target.value as JournalStatus | '')}>
            <option value="">All</option>
            <option value="Draft">Draft</option>
            <option value="Posted">Posted</option>
            <option value="Reversed">Reversed</option>
          </select>
        </div>
        <div className="field">
          <label>Basis</label>
          <select value={basis} onChange={(e) => setBasis(e.target.value as Basis | '')}>
            <option value="">All</option>
            <option value="Both">Both</option>
            <option value="AccrualOnly">Accrual only</option>
            <option value="CashOnly">Cash only</option>
          </select>
        </div>
        <div className="field">
          <label>Search</label>
          <input value={term} onChange={(e) => setTerm(e.target.value)} placeholder="Number or text" />
        </div>

        {/* Only worth offering where it means something. With review switched off a draft is
            simply one nobody has posted yet, and a queue would imply a step that does not exist. */}
        {policy.data?.requireReviewBeforePost && (
          <div className="field">
            <label>&nbsp;</label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 'normal' }}>
              <input
                type="checkbox"
                checked={waitingOnly}
                onChange={(e) => setWaitingOnly(e.target.checked)}
              />
              Awaiting review{waiting.length > 0 && ` (${waiting.length})`}
            </label>
          </div>
        )}

        <div className="spacer" />

        {can(P.journal.create) && (
          <button className="primary" onClick={() => setComposing(true)}>
            + New entry
          </button>
        )}
      </div>

      <ErrorBanner error={entries.error} />
      <ErrorBanner error={reviewError} onDismiss={() => setReviewError(undefined)} />

      {/* The reviewer's own prompt. Without it the queue is a checkbox nobody has a reason to tick,
          and the drafts sit there because posting them is somebody else's screen. */}
      {policy.data?.requireReviewBeforePost && !waitingOnly && waiting.length > 0 && (
        <div className="banner info" style={{ marginBottom: 14 }}>
          {waiting.length === 1 ? '1 draft is' : `${waiting.length} drafts are`} waiting for a second
          person before {waiting.length === 1 ? 'it' : 'they'} can be posted.{' '}
          <button className="subtle small" onClick={() => setWaitingOnly(true)}>
            Show {waiting.length === 1 ? 'it' : 'them'}
          </button>
        </div>
      )}

      {entries.loading && <Loading />}

      {entries.data && (
        <Panel flush>
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th style={{ width: 90 }}>Date</th>
                  <th style={{ width: 130 }}>Number</th>
                  <th style={{ width: 80 }}>Book</th>
                  <th>Description</th>
                  <th style={{ width: 110 }}>Basis</th>
                  <th style={{ width: 90 }}>Status</th>
                  {policy.data?.requireReviewBeforePost && <th style={{ width: 150 }}>Review</th>}
                  <th style={{ width: 130 }} className="num">
                    Amount
                  </th>
                  <th style={{ width: 160 }} />
                </tr>
              </thead>
              <tbody>
                {shown.length === 0 && (
                  <tr>
                    <td colSpan={policy.data?.requireReviewBeforePost ? 9 : 8}>
                      <Empty>
                        {waitingOnly ? 'Nothing is waiting for review.' : 'No entries match.'}
                      </Empty>
                    </td>
                  </tr>
                )}

                {shown.map((entry) => (
                  <tr key={entry.id}>
                    <td>{formatDate(entry.postingDate)}</td>
                    <td className="code">
                      <Link to={`/journal/${entry.id}`}>{entry.entryNo ?? 'draft'}</Link>
                    </td>
                    <td>{entry.book}</td>
                    <td>{entry.description}</td>
                    <td>
                      {/* The dual-basis flag is the single most important field on an entry, so it
                          is on the list rather than hidden behind a click. */}
                      <span className={entry.basis === 'Both' ? 'tag' : 'tag warn'}>{entry.basis}</span>
                    </td>
                    <td>
                      <StatusTag status={entry.status} />
                    </td>

                    {policy.data?.requireReviewBeforePost && (
                      <td>
                        {entry.reviewedAt ? (
                          <span className="tag ok" title={`${entry.reviewedBy ?? ''} · ${formatDateTime(entry.reviewedAt)}`}>
                            Reviewed
                          </span>
                        ) : entry.status === 'Draft' ? (
                          <span className="tag warn">Awaiting</span>
                        ) : (
                          <span className="muted">—</span>
                        )}
                      </td>
                    )}

                    <td className="num">
                      <Money value={entry.amount} />
                    </td>
                    <td>
                      {entry.status === 'Posted' && can(P.journal.reverse) && (
                        <button className="subtle small" onClick={() => setReversing(entry)}>
                          Reverse
                        </button>
                      )}

                      {/* A reviewer may not sign off their own entry — the server refuses it, and
                          offering a button that always fails is worse than not offering one. The
                          author is compared on the same identifier the row was written with. */}
                      {entry.status === 'Draft' && can(P.journal.review) && !entry.reviewedAt && (
                        <button
                          className="subtle small"
                          disabled={!!entry.createdBy && entry.createdBy === user?.email}
                          title={
                            entry.createdBy === user?.email
                              ? 'An entry must be reviewed by someone other than its author.'
                              : undefined
                          }
                          onClick={() => void review(entry, true)}
                        >
                          Review
                        </button>
                      )}

                      {entry.status === 'Draft' && can(P.journal.review) && entry.reviewedAt && (
                        <button className="subtle small" onClick={() => void review(entry, false)}>
                          Withdraw
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      )}

      {composing && (
        <ComposeEntry
          requiresReview={policy.data?.requireReviewBeforePost ?? false}
          onClose={() => setComposing(false)}
          onSaved={() => {
            setComposing(false)
            entries.reload()
          }}
        />
      )}

      {reversing && (
        <ReverseEntry
          entry={reversing}
          onClose={() => setReversing(null)}
          onSaved={() => {
            setReversing(null)
            entries.reload()
          }}
        />
      )}
    </>
  )
}

interface DraftLine {
  accountId: string
  debit: string
  credit: string
  partyId: string
  nature: ClientFundNature | ''
  /** The cost centre, on the income and expense lines only (BUILD_PLAN §3.5). */
  departmentId: string
  memo: string
  /** The open item this line clears, if it clears one. */
  settlesOpenItemId: string
}

const EMPTY_LINE: DraftLine = {
  accountId: '',
  debit: '',
  credit: '',
  partyId: '',
  nature: '',
  departmentId: '',
  memo: '',
  settlesOpenItemId: '',
}

function ComposeEntry({
  onClose,
  onSaved,
  requiresReview,
}: {
  onClose: () => void
  onSaved: () => void
  /** When the office requires four eyes, this screen can only ever produce a draft. */
  requiresReview: boolean
}) {
  const { can } = useAuth()
  const accounts = useQuery<PostableAccount[]>('/accounts/postable')
  const parties = useQuery<Party[]>('/parties')

  // The composer's account picker deliberately does not list the per-party sub-ledger accounts —
  // a hand-typed entry must not land in a balance the sub-ledger controls. It reaches them the
  // other way round: pick the document, and the account and the party come from the document. So
  // the account is still never chosen by hand here.
  const openItems = useQuery<OpenItem[]>('/open-items' + query({ onlyOpen: true }))

  // Active only, for the same reason the account list is: an inactive cost centre is refused by
  // the posting engine, so offering one would be offering something the server then rejects.
  const departments = useQuery<Department[]>('/departments')

  const [postingDate, setPostingDate] = useState(isoDate(new Date()))
  const [basis, setBasis] = useState<Basis>('AccrualOnly')
  const [description, setDescription] = useState('')
  const [reference, setReference] = useState('')
  const [autoReverse, setAutoReverse] = useState(false)
  const [lines, setLines] = useState<DraftLine[]>([{ ...EMPTY_LINE }, { ...EMPTY_LINE }])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>()
  const [validation, setValidation] = useState<PostingValidation | null>(null)

  const accountsById = useMemo(
    () => new Map((accounts.data ?? []).map((a) => [a.id, a])),
    [accounts.data],
  )

  const openItemsById = useMemo(
    () => new Map((openItems.data ?? []).map((i) => [i.id, i])),
    [openItems.data],
  )

  const totalDebit = lines.reduce((sum, l) => sum + (Number(l.debit) || 0), 0)
  const totalCredit = lines.reduce((sum, l) => sum + (Number(l.credit) || 0), 0)
  const balanced = totalDebit > 0 && Math.abs(totalDebit - totalCredit) < 0.005

  function update(index: number, patch: Partial<DraftLine>) {
    setLines((previous) => previous.map((line, i) => (i === index ? { ...line, ...patch } : line)))
  }

  /**
   * The side this line sits on, which is the side an open item has to be settled from. Null until
   * an amount is typed — at which point the list narrows to what this line could actually clear.
   */
  function sideOf(line: DraftLine): 'Debit' | 'Credit' | null {
    if (Number(line.debit) > 0) return 'Debit'
    if (Number(line.credit) > 0) return 'Credit'
    return null
  }

  /**
   * What this line could clear. Before an amount is typed both sides are offered, because the
   * usual order is to pick the document and let it fill the amount in.
   *
   * The document already chosen stays in the list whichever side the amount ends up on. Dropping it
   * would leave the select showing nothing while the line still carried the reference, and the
   * first anyone heard of it would be the server refusing the post.
   */
  function settleable(line: DraftLine): OpenItem[] {
    const side = sideOf(line)

    return (openItems.data ?? []).filter(
      (item) => side === null || item.settledOn === side || item.id === line.settlesOpenItemId,
    )
  }

  /** Picking a document decides the account and the party; neither is typed. */
  function settle(index: number, itemId: string) {
    const item = openItemsById.get(itemId)

    if (!item) {
      update(index, { settlesOpenItemId: '', accountId: '', partyId: '' })
      return
    }

    const line = lines[index]
    const amount = line.debit || line.credit ? '' : String(item.outstanding)

    update(index, {
      settlesOpenItemId: itemId,
      accountId: item.accountId ?? '',
      partyId: item.partyId,
      nature: item.nature ?? '',

      // A document always sits on a sub-ledger account, which is a balance-sheet account, so a
      // cost centre chosen a moment ago would now be one the server refuses.
      departmentId: '',
      // The usual case is clearing the document in full, so offer that figure rather than making
      // it be typed twice. It lands on the side the document is settled from.
      debit: amount && item.settledOn === 'Debit' ? amount : line.debit,
      credit: amount && item.settledOn === 'Credit' ? amount : line.credit,
    })
  }

  function payload() {
    return {
      book: 'General' as const,
      postingDate,
      basis,
      description,
      reference: reference || null,
      autoReverseNextPeriod: autoReverse,
      lines: lines
        .filter((l) => l.accountId && (l.debit || l.credit))
        .map((l) => ({
          accountId: l.accountId,
          debit: Number(l.debit) || 0,
          credit: Number(l.credit) || 0,
          partyId: l.partyId || null,
          clientFundNature: l.nature || null,

          // Cleared when the account changes, so a cost centre picked against rent does not
          // survive the line being repointed at a balance-sheet account, where it is refused.
          departmentId: l.departmentId || null,

          memo: l.memo || null,
          settlesOpenItemId: l.settlesOpenItemId || null,
        })),
    }
  }

  /** Runs every server rule without writing anything, so problems show before the post button. */
  async function check() {
    setError(undefined)
    try {
      setValidation(await api<PostingValidation>('/journal/validate', { method: 'POST', body: payload() }))
    } catch (caught) {
      setError(caught)
    }
  }

  async function submit(post: boolean) {
    setBusy(true)
    setError(undefined)

    try {
      await api(post ? '/journal/post' : '/journal/draft', { method: 'POST', body: payload() })
      onSaved()
    } catch (caught) {
      setError(caught)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      title="New journal entry"
      width="wide"
      onClose={onClose}
      footer={
        <>
          <span className={balanced ? 'positive' : 'negative'} style={{ marginRight: 'auto', fontSize: 13 }}>
            Dr {money(totalDebit)} · Cr {money(totalCredit)}
            {!balanced && ` · out by ${money(Math.abs(totalDebit - totalCredit))}`}
          </span>
          <button onClick={() => void check()}>Check</button>

          {/* Under a review requirement the draft is the only outcome this screen has, so it is the
              primary action rather than the fallback — and Post is not offered at all, because the
              server refuses it and a button whose only outcome is an error is not a choice. */}
          <button
            className={requiresReview ? 'primary' : undefined}
            onClick={() => void submit(false)}
            disabled={busy || (requiresReview && !description)}
          >
            {busy && requiresReview ? 'Saving…' : 'Save draft'}
          </button>

          {can(P.journal.post) && !requiresReview && (
            <button className="primary" disabled={busy || !balanced || !description} onClick={() => void submit(true)}>
              {busy ? 'Posting…' : 'Post'}
            </button>
          )}
        </>
      }
    >
      <ErrorBanner error={error} onDismiss={() => setError(undefined)} />

      {requiresReview && (
        <div className="banner info">
          This office requires a second person to review journal entries. Saving leaves it as a
          draft on the review queue; whoever reviews it cannot be you.
        </div>
      )}

      {validation && (
        <div className={validation.isValid ? 'banner ok' : 'banner warn'}>
          <div>
            {validation.isValid ? (
              <strong>
                {requiresReview
                  ? 'Every rule passes. This entry will post once it is reviewed.'
                  : 'Every rule passes. This entry will post.'}
              </strong>
            ) : (
              <>
                <strong>{validation.problems.length} problem(s):</strong>
                <ul>
                  {validation.problems.map((problem, index) => (
                    <li key={index}>
                      {problem.lineNo ? `Line ${problem.lineNo}: ` : ''}
                      {problem.message}
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        </div>
      )}

      <div className="toolbar" style={{ marginBottom: 14 }}>
        <div className="field">
          <label>Posting date</label>
          <input type="date" value={postingDate} onChange={(e) => setPostingDate(e.target.value)} />
        </div>

        <div className="field">
          <label>Basis</label>
          <select value={basis} onChange={(e) => setBasis(e.target.value as Basis)}>
            <option value="AccrualOnly">Accrual only — accrual, no cash moved</option>
            <option value="Both">Both — actual cash movement</option>
            <option value="CashOnly">Cash only — timing adjustment</option>
          </select>
        </div>

        <div className="field">
          <label>Reference</label>
          <input value={reference} onChange={(e) => setReference(e.target.value)} />
        </div>

        <label style={{ textTransform: 'none', fontWeight: 400, fontSize: 13, paddingBottom: 6 }}>
          <input
            type="checkbox"
            style={{ width: 'auto', marginRight: 6 }}
            checked={autoReverse}
            onChange={(e) => setAutoReverse(e.target.checked)}
          />
          Reverse next period
        </label>
      </div>

      <Field label="Description">
        <input
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Monthly salary accrual"
        />
      </Field>

      <p className="muted" style={{ fontSize: 12, marginBottom: 6 }}>
        To clear an invoice, a custody issue or a loan, name it under <strong>Settles</strong> — the
        account and the party come from the document, and the amount defaults to what is left on it.
        A line that settles nothing is an ordinary entry.
      </p>

      <div className="table-scroll">
        <table>
          <thead>
            <tr>
              <th>Account</th>
              <th style={{ width: 130 }} className="num">
                Debit
              </th>
              <th style={{ width: 130 }} className="num">
                Credit
              </th>
              <th style={{ width: 150 }}>Party</th>
              <th style={{ width: 130 }}>Nature</th>
              <th style={{ width: 150 }}>Cost centre</th>
              <th style={{ width: 190 }}>Settles</th>
              <th>Memo</th>
              <th style={{ width: 36 }} />
            </tr>
          </thead>
          <tbody>
            {lines.map((line, index) => {
              const account = accountsById.get(line.accountId)
              const settling = line.settlesOpenItemId
                ? openItemsById.get(line.settlesOpenItemId)
                : undefined
              const open = settleable(line)

              // The same rule the posting engine applies. A settling line always names a
              // sub-ledger account, so it is never one of these.
              const isPandL = account?.type === 'Revenue' || account?.type === 'Expense'

              return (
                <tr key={index}>
                  <td>
                    {/* When the line settles a document the account came from the document, and it
                        is a sub-ledger account this picker deliberately does not list. Showing it
                        as text rather than an unmatched select keeps that honest. */}
                    {settling ? (
                      <span className="code">
                        {settling.accountCode} · {settling.accountName}
                      </span>
                    ) : (
                      <select
                        value={line.accountId}
                        onChange={(e) =>
                          update(index, { accountId: e.target.value, nature: '', departmentId: '' })
                        }
                      >
                        <option value="">Choose an account…</option>
                        {accounts.data?.map((a) => (
                          <option key={a.id} value={a.id}>
                            {a.code} · {a.nameEn}
                          </option>
                        ))}
                      </select>
                    )}
                  </td>

                  <td>
                    <input
                      className="num"
                      inputMode="decimal"
                      value={line.debit}
                      onChange={(e) => update(index, { debit: e.target.value, credit: '' })}
                    />
                  </td>

                  <td>
                    <input
                      className="num"
                      inputMode="decimal"
                      value={line.credit}
                      onChange={(e) => update(index, { credit: e.target.value, debit: '' })}
                    />
                  </td>

                  <td>
                    {settling ? (
                      <span>{settling.partyName}</span>
                    ) : account?.partyRequired ? (
                      <select value={line.partyId} onChange={(e) => update(index, { partyId: e.target.value })}>
                        <option value="">Required…</option>
                        {parties.data?.map((party) => (
                          <option key={party.id} value={party.id}>
                            {party.nameEn}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <span className="muted">—</span>
                    )}
                  </td>

                  <td>
                    {settling ? (
                      <span className="muted">{settling.nature ?? '—'}</span>
                    ) : account?.natureRequired ? (
                      <select
                        value={line.nature}
                        onChange={(e) => update(index, { nature: e.target.value as ClientFundNature | '' })}
                      >
                        <option value="">Required…</option>
                        <option value="Fee">Fee</option>
                        <option value="Custody">Custody</option>
                        <option value="Settlement">Settlement</option>
                        <option value="Advance">Advance</option>
                        <option value="CreditNote">Credit note</option>
                        <option value="DebitNote">Debit note</option>
                      </select>
                    ) : (
                      <span className="muted">—</span>
                    )}
                  </td>

                  <td>
                    {/* The second analysis dimension (BUILD_PLAN §3.5), on the income and expense
                        lines only. The G&A analysis is the report that reads it and that is a
                        profit-and-loss report, so a cost centre against a bank or a client balance
                        would be typed and then aggregated by nothing. The server refuses it there,
                        which is why the field is not offered. */}
                    {isPandL && departments.data && departments.data.length > 0 ? (
                      <select
                        value={line.departmentId}
                        onChange={(e) => update(index, { departmentId: e.target.value })}
                      >
                        <option value="">—</option>
                        {departments.data.map((department) => (
                          <option key={department.id} value={department.id}>
                            {department.code} · {department.nameEn}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <span className="muted">—</span>
                    )}
                  </td>

                  <td>
                    {/* The engine has always accepted a settlement reference from the journal as
                        readily as from the books; only this field was missing. Naming the document
                        is what keeps the aging report from listing everything ever billed. */}
                    <select
                      value={line.settlesOpenItemId}
                      onChange={(e) => settle(index, e.target.value)}
                    >
                      <option value="">Settles nothing</option>
                      {open.map((item) => (
                        <option key={item.id} value={item.id}>
                          {item.partyName} · {item.docNo} · {money(item.outstanding)}
                          {item.currency === 'EGP' ? '' : ` ${item.currency}`}
                          {item.daysOverdue ? ` · ${item.daysOverdue}d overdue` : ''}
                        </option>
                      ))}
                    </select>
                  </td>

                  <td>
                    <input value={line.memo} onChange={(e) => update(index, { memo: e.target.value })} />
                  </td>

                  <td>
                    {lines.length > 2 && (
                      <button
                        className="subtle"
                        onClick={() => setLines((previous) => previous.filter((_, i) => i !== index))}
                      >
                        ✕
                      </button>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
          <tfoot>
            <tr>
              <td>
                <button className="subtle" onClick={() => setLines((p) => [...p, { ...EMPTY_LINE }])}>
                  + Add line
                </button>
              </td>
              <td className="num">{money(totalDebit)}</td>
              <td className="num">{money(totalCredit)}</td>
              <td colSpan={6} />
            </tr>
          </tfoot>
        </table>
      </div>
    </Modal>
  )
}

/**
 * Reversing a posted entry, from the list or from the detail page.
 *
 * Takes only what it needs rather than a whole summary, so the detail page can hand it a
 * `JournalEntryDetail` without the two shapes having to be made to agree.
 */
export function ReverseEntry({
  entry,
  onClose,
  onSaved,
}: {
  entry: { id: string; entryNo?: string | null }
  onClose: () => void
  onSaved: () => void
}) {
  const [reversalDate, setReversalDate] = useState(isoDate(new Date()))
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>()

  async function submit() {
    setBusy(true)
    setError(undefined)

    try {
      await api(`/journal/${entry.id}/reverse`, { method: 'POST', body: { reversalDate, reason } })
      onSaved()
    } catch (caught) {
      setError(caught)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      title={`Reverse ${entry.entryNo}`}
      width="narrow"
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose}>Cancel</button>
          <button className="danger" disabled={busy || !reason.trim()} onClick={() => void submit()}>
            {busy ? 'Reversing…' : 'Reverse'}
          </button>
        </>
      }
    >
      <ErrorBanner error={error} onDismiss={() => setError(undefined)} />

      <div className="banner info">
        A posted entry is never edited or deleted. This writes a mirror entry with the debits and
        credits swapped; both stay in the ledger permanently.
      </div>

      <Field label="Reversal date" hint="Must fall in an open period.">
        <input type="date" value={reversalDate} onChange={(e) => setReversalDate(e.target.value)} />
      </Field>

      <Field label="Reason" hint="Recorded permanently on both entries and in the audit log.">
        <input
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Charged to the wrong month"
        />
      </Field>
    </Modal>
  )
}
